/* The book's value over time — the first thing the app shows.
 *
 * A ring tells you how the money is split; a line tells you where it has been.
 * The second is the question you have on opening the app, so it leads, and the
 * split moved to its own screen. */

import { useEffect, useMemo, useState } from 'react';
import {
  PerformanceChart, type ChartMode, type CustomRange, type RangeKey, type ScrubPoint,
} from './PerformanceChart';
import { ageLabel, money, moneySigned, pctSigned, shortDate, stamp } from '../lib/format';
import { perDayResolution, RANGE_LABEL, summariseRange } from '../state/series';
import { BENCHMARKS, benchmarksFrom } from '../state/derive';
import { useStore } from '../state/store';

const RANGES: RangeKey[] = ['1D', '1W', '1M', '3M', 'YTD', 'All', 'Custom'];

export function ValuePanel({
  toDisplay, currency, onOpenDeposits, onOpenResults, onShowSplit,
  compact = false, initialRange, initialCustom, onRangeChange,
}: {
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
  /** Opens the results screen — realised figures and the standings. */
  onOpenResults?: () => void;
  /** Opens the breakdown below and moves to it. The total is the thing people
      tap when they want to know what it is made of, and until now tapping it
      did nothing at all. */
  onShowSplit?: () => void;
  /** The money-paid-in line is blank until deposits exist, and the settings
      screen is a long way from here. The sentence saying so is the button. */
  onOpenDeposits?: () => void;
  /* The card as the Value screen shows it: the figure, the move, the line and
     the windows, and nothing else. Everything that explains or compares — money
     paid in, the indices, the other two modes, results, the rate — lives on the
     full card one tap away, so the first screen is read in a glance. */
  compact?: boolean;
  /** The window to open on, when arriving from the compact card. */
  initialRange?: RangeKey;
  initialCustom?: CustomRange;
  /** Told whenever the window changes, so it can be carried to the full card. */
  onRangeChange?: (range: RangeKey, custom: CustomRange) => void;
}) {
  const { book, state, loadDailyCloses, saveSettings } = useStore();
  const [mode, setMode] = useState<ChartMode>('value');
  const [range, setRange] = useState<RangeKey>(
    () => (initialRange && RANGES.includes(initialRange) ? initialRange : '3M'),
  );
  const [custom, setCustom] = useState<CustomRange>(() => initialCustom ?? {
    from: new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
  });
  useEffect(() => { onRangeChange?.(range, custom); }, [range, custom]);
  // The compact card draws value only; the other readings are on the full one.
  const chartMode: ChartMode = compact ? 'value' : mode;
  const [scrub, setScrub] = useState<ScrubPoint | null>(null);
  /* Absent means off, and that is a change of mind rather than an oversight.
     The band between value and money-paid-in is the right picture over a year,
     where the two lines move together. Over a week it is not: a book worth
     $58k against $43k paid in stretches the scale across fifteen thousand
     dollars to draw a line that moved one percent, so the week comes out as a
     flat lid over a solid block and every night and gap in it reads as a cliff.
     The line is one tap away for anyone who wants the band back. */
  const showPaidIn = !compact && state.settings.chartPaidIn === true;

  /* The lines drawn against the book. Two years of closes each, fetched once
     and cut to whichever window is on screen. Keyed on the joined list so
     adding or removing one refetches only when the choice actually changed. */
  const chosen = benchmarksFrom(state.settings);
  // Nothing to fetch for a card that does not draw them.
  const chosenKey = compact ? '' : chosen.join(',');
  const [benchSeries, setBenchSeries] = useState<Array<{ label: string; days: Array<{ date: string; close: number }> }>>([]);
  /* Whether they are on their way. Two years of closes for four symbols is a
     second or two on a cold cache, and without saying so the row simply is not
     there — which reads as the feature being broken rather than as it arriving.
     `failed` is the same argument for the other outcome. */
  const [benchLoading, setBenchLoading] = useState(false);
  const [benchFailed, setBenchFailed] = useState<string[]>([]);
  useEffect(() => {
    const symbols = chosenKey === '' ? [] : chosenKey.split(',');
    if (symbols.length === 0) { setBenchSeries([]); setBenchFailed([]); setBenchLoading(false); return; }
    let live = true;
    setBenchLoading(true);
    void Promise.all(symbols.map(async (symbol) => ({
      // The name, not the ticker: the legend is read by someone asking whether
      // they beat the S&P, not by someone matching symbols.
      label: BENCHMARKS.find((b) => b.id === symbol)?.label ?? symbol,
      days: await loadDailyCloses(symbol).catch(() => []),
    }))).then((loaded) => {
      if (!live) return;
      // A comparison that would not load is named rather than silently missing.
      setBenchSeries(loaded.filter((b) => b.days.length > 1));
      setBenchFailed(loaded.filter((b) => b.days.length <= 1).map((b) => b.label));
      setBenchLoading(false);
    });
    return () => { live = false; };
  }, [chosenKey, loadDailyCloses]);

  const { totals } = book;
  const staleness = ageLabel(book.marksAsOf);
  const headline = scrub ? scrub.totalUsd : totals.totalUsd;

  /* The figure under the total follows the window the buttons are set to. It
     used to report today's move whatever the chart was showing, so choosing 3M
     left a three-month line sitting over a one-day number. Today keeps its own
     reading — that one comes from each position's previous close and is exact —
     and every longer window is measured from the oldest point inside it. */
  const summary = useMemo(
    () => summariseRange(
      book.timeline, range, custom, totals.totalUsd, new Date(), totals.contributedUsd,
    ),
    [book.timeline, range, custom, totals.totalUsd, totals.contributedUsd],
  );
  const overRange = range !== '1D' && summary !== null;

  /* Over a window, the figure is the market's work with deposits taken out.
   *
   * The plain change counts every deposit as a gain — pay in ten thousand over
   * a year and the line climbs by ten thousand without a share moving, and the
   * percentage calls that a return. It is the same wrong denominator the
   * scrubbed figure was fixed for, and the year is where it does the most
   * damage because it is the window most money arrives in.
   *
   * Today keeps its own reading: a day's move comes from each position's
   * previous close and is exact, and a deposit inside it is already excluded by
   * how that figure is built. */
  const netted = overRange && summary!.paidInKnown;
  const changeUsd = !overRange ? totals.dayChangeUsd
    : netted ? summary!.marketChangeUsd : summary!.changeUsd;
  const changePct = !overRange ? totals.dayChangePct
    : netted ? summary!.marketChangePct : summary!.changePct;

  /* What each comparison did over the window on screen, measured the same way
     the book's own figure is: first reading inside the window against the last.
     Without this the lines answer "did I beat it" only by eye, and by eye two
     rebased curves a few percent apart are the same curve. */
  const benchReturns = useMemo(() => {
    if (!overRange || summary === null) return [];
    const from = summary.baseAt.slice(0, 10);
    return benchSeries.map((b) => {
      const inside = b.days.filter((d) => d.date >= from);
      const first = inside[0]?.close;
      const last = inside[inside.length - 1]?.close;
      if (!first || !last || first <= 0) return { label: b.label, pct: null };
      return { label: b.label, pct: (last / first - 1) * 100 };
    });
  }, [benchSeries, overRange, summary]);
  const periodLabel = !overRange ? 'today'
    : range === 'All' ? `since ${shortDate(summary!.baseAt)}`
    : range === 'Custom' ? `${shortDate(custom.from)} to ${shortDate(custom.to)}`
    : RANGE_LABEL[range as Exclude<RangeKey, 'All' | 'Custom'>];

  return (
    <div className="card hero">
      <div className="hero-label">
        {scrub ? stamp(scrub.at, !perDayResolution(range, custom))
          : totals.unpricedCount > 0 ? 'Value · part at cost'
          : 'Value'}
      </div>
      {onShowSplit ? (
        <button className="hero-total num" onClick={onShowSplit}
                style={{ display: 'block', textAlign: 'left', width: '100%' }}
                aria-label="What this is made of">
          {money(toDisplay(headline), currency)}
        </button>
      ) : (
        <div className="hero-total num">{money(toDisplay(headline), currency)}</div>
      )}

      <div className="hero-change">
        {/* Pointing at a day asks a different question from looking at a
            window, and it deserves a different answer.
            *
            * The window figure is the book against itself at the left edge,
            * which is the wrong denominator the moment money went in during it:
            * pay in ten thousand and the line climbs by ten thousand without a
            * single share doing anything. Against what was paid in by that day,
            * the percentage is the market's work and only the market's.
            *
            * And the figure it is measured against goes on screen beside it. A
            * percentage whose denominator is out of sight is a number nobody can
            * check, and this one moves every time money goes in. */}
        {scrub ? (
          <>
            {/* The growth, whether or not the line it is measured against is
                being drawn. Hiding that line is a decision about the chart, not
                about what is worth knowing — and what the market added is the
                thing being pointed at. */}
            <span className={`num ${scrub.gainUsd >= 0 ? 'up' : 'down'}`}>
              {scrub.gainUsd >= 0 ? '▲' : '▼'}{' '}
              {moneySigned(toDisplay(scrub.gainUsd), currency)}
              {scrub.contributedUsd > 0 ? `  ${pctSigned(scrub.returnPct)}` : ''}
            </span>
            {compact ? null : (
              <span className="dim" style={{ fontSize: 12 }}>
                {scrub.contributedUsd > 0
                  ? `on ${money(toDisplay(scrub.contributedUsd), currency)} paid in`
                  : 'no cash-flow record for this date, so there is nothing to measure against'}
              </span>
            )}
          </>
        ) : (
          <>
            {range !== '1D' && summary === null ? (
              <span className="dim" style={{ fontSize: 12 }}>
                Nothing recorded in this window, so there is no move to report.
              </span>
            ) : (
              <>
                <span className={`num ${changeUsd >= 0 ? 'up' : 'down'}`}>
                  {changeUsd >= 0 ? '▲' : '▼'}{' '}
                  {moneySigned(toDisplay(changeUsd), currency)}
                  {'  '}{pctSigned(changePct)}
                </span>
                {compact ? null : <span className="dim" style={{ fontSize: 12 }}>{periodLabel}</span>}
                {/* What was netted out, named. A percentage whose denominator
                    is out of sight is a number nobody can check, and this one
                    moves every time money goes in. */}
                {!compact && netted && Math.abs(summary!.paidInUsd) >= 1 ? (
                  <span className="dim" style={{ fontSize: 11 }}>
                    {summary!.paidInUsd > 0 ? 'after ' : 'after taking out '}
                    <span className="num">
                      {money(toDisplay(Math.abs(summary!.paidInUsd)), currency)}
                    </span>
                    {summary!.paidInUsd > 0 ? ' paid in over it' : ' withdrawn over it'}
                  </span>
                ) : null}
                {!compact && overRange && !summary!.paidInKnown && totals.contributedUsd > 0 ? (
                  <span className="dim" style={{ fontSize: 11 }}>
                    no cash-flow record at the start of this window, so any money paid in during it
                    is still counted in the figure above
                  </span>
                ) : null}
              </>
            )}
            {staleness ? (
              <span className="stale-note">
                <span aria-hidden="true">⏱</span> prices from {staleness}
                {/* Named when one listing is behind the rest, so the line is
                    something you can act on rather than something to distrust. */}
                {book.marksLaggard ? `, ${book.marksLaggard} is the one lagging` : ''}
              </span>
            ) : null}
            {book.session.extended ? (
              <span className="stale-note">
                <span aria-hidden="true">⏱</span> on {book.session.label.toLowerCase()} prices,
                and the chart records the close
              </span>
            ) : null}
            {/* A shut exchange priced from the contract MEXC lists on the same
                company says nothing here on purpose.
                *
                * The reading is a reading: the contract's index price tracks the
                * share to inside a percent, the switch in settings governs
                * whether it is used at all, and the chart still records the
                * close either way. A line on the headline announcing the
                * plumbing made a normal price look like a caveat. Settings names
                * the positions it touched, which is where somebody asking the
                * question actually goes. */}
          </>
        )}
      </div>

      {/* Reading the book in złoty means every figure has been through a rate.
          Which rate, and when it was published, has to be visible. */}
      {!compact && currency === 'PLN' ? (
        <div className="fx-note">
          {state.fx?.usdPln ? (
            <>
              <span className="num">1 USD = {state.fx.usdPln.toFixed(4)} PLN</span>
              <span className="dim">
                NBP table of {shortDate(state.fx.publishedOn)}
                {state.fx.publishedOn !== new Date().toISOString().slice(0, 10)
                  ? ', and it only moves on business days'
                  : ''}
              </span>
            </>
          ) : (
            <span className="warn">No rate yet, so these are still dollars.</span>
          )}
        </div>
      ) : null}

      {state.lastRefreshError ? (
        <div className="stale-note" style={{ marginTop: 6, display: 'flex' }}>
          <span aria-hidden="true">⚠</span> {state.lastRefreshError}. Showing the last marks received.
        </div>
      ) : null}

      <div style={{ marginTop: 18 }}>
        <PerformanceChart
          snapshots={book.timeline}
          benchmarks={benchSeries}
          mode={chartMode}
          range={range}
          custom={custom}
          currency={currency}
          toDisplay={toDisplay}
          onScrub={setScrub}
        showPaidIn={showPaidIn}
        />

        <div className="range-row">
          {(['1D', '1W', '1M', '3M', 'YTD', 'All', 'Custom'] as const).map((r) => (
            <button key={r} aria-pressed={range === r} onClick={() => setRange(r)}>{r}</button>
          ))}
        </div>

        {range === 'Custom' ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input className="input" type="date" value={custom.from} aria-label="From date"
                   onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
            <input className="input" type="date" value={custom.to} aria-label="To date"
                   onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
          </div>
        ) : null}

        {/* How each comparison did over the same window, in figures.
            *
            * The lines answer "did I beat it" only by eye, and by eye two
            * rebased curves a few percent apart are the same curve. The
            * book's own number sits first so the reading is a comparison
            * rather than four numbers to hold in your head. */}
        {!compact && overRange && (benchLoading || benchReturns.length > 0 || benchFailed.length > 0) ? (
          <div className="kv-list" style={{ marginTop: 12 }}>
            <div className="kv">
              <span style={{ fontWeight: 600 }}>This book</span>
              <span className={`num ${changePct >= 0 ? 'up' : 'down'}`}
                    style={{ marginLeft: 'auto', fontWeight: 600 }}>
                {pctSigned(changePct)}
              </span>
            </div>
            {benchReturns.map((b) => (
              <div className="kv" key={b.label}>
                <span className="dim">{b.label}</span>
                <span className={`num ${b.pct === null ? 'dim' : b.pct >= 0 ? 'up' : 'down'}`}
                      style={{ marginLeft: 'auto' }}>
                  {b.pct === null ? 'no history in this window' : pctSigned(b.pct)}
                </span>
              </div>
            ))}
            {benchLoading ? (
              <div className="kv">
                <span className="dim">Fetching the comparisons…</span>
              </div>
            ) : null}
            {benchFailed.length > 0 ? (
              <div className="hint hint-warn">
                {benchFailed.join(', ')} would not load, so {benchFailed.length === 1 ? 'it is' : 'they are'}{' '}
                left off rather than drawn flat. Pull down to try again.
              </div>
            ) : null}
          </div>
        ) : null}

        {/* The lines drawn against the book, switched on where the chart is
            rather than three screens away in settings.
            *
            * It was only ever in settings, which is the wrong place for it: the
            * question "did I beat the index" arrives while looking at the line,
            * not while configuring the app, and by the time you have found the
            * setting you are no longer looking at what prompted it. Same
            * setting underneath, so the two screens can never disagree. */}
        {compact ? null : (
        <div className="range-row" aria-label="Compare against">
          <span className="dim" style={{ flex: '0 0 auto', fontSize: 11, alignSelf: 'center', paddingRight: 2 }}>
            vs
          </span>
          {BENCHMARKS.map((b) => (
            <button
              key={b.id}
              aria-pressed={chosen.includes(b.id)}
              title={b.label}
              onClick={() => void saveSettings({
                benchmarks: chosen.includes(b.id)
                  ? chosen.filter((c) => c !== b.id)
                  : [...chosen, b.id],
              })}
            >
              {b.short}
            </button>
          ))}
        </div>
        )}

        {/* What the trading produced, as against what the book is worth. Behind
            a tap because it answers a different question from the line above
            it, and putting both on one screen made each harder to read. */}
        {!compact && onOpenResults ? (
          <button className="btn btn-ghost btn-sm btn-block" style={{ marginTop: 10 }}
                  onClick={onOpenResults}>
            Results · what the trading made ›
          </button>
        ) : null}

        {compact ? null : (
        <div className="seg" style={{ marginTop: 12 }}>
          {(['value', 'gains', 'return'] as const).map((m) => (
            <button key={m} aria-pressed={mode === m} onClick={() => setMode(m)}>
              {m === 'value' ? 'Value' : m === 'gains' ? 'Gains only' : 'Return %'}
            </button>
          ))}
        </div>
        )}

        {/* The money-paid-in line, on or off, where the chart is.
            *
            * With it, the chart answers "what did the market add on top of what
            * I put in" — the filled band is that answer. Without it, the value
            * line has the whole scale to itself and the shape of the book comes
            * out larger. Both are worth having, and which one is wanted changes
            * with what is being looked at rather than with a setting made once.
            * Only in the value mode: the other two are already a difference, so
            * a second line for what went in would be drawn against a quantity
            * it does not belong to. */}
        {!compact && mode === 'value' ? (
          <button
            className="btn btn-ghost btn-sm btn-block"
            style={{ marginTop: 8 }}
            aria-pressed={showPaidIn}
            onClick={() => void saveSettings({ chartPaidIn: !showPaidIn })}
          >
            {showPaidIn ? 'Hide money paid in' : 'Show money paid in'}
          </button>
        ) : null}

        {!compact && overRange && summary!.baseReconstructed ? (
          <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.45 }}>
            Measured from {shortDate(summary!.baseAt)}, a point rebuilt from that day's closing
            prices rather than recorded, so this is what the market did to today's holdings, not
            what the account itself did.
          </div>
        ) : null}

        {compact ? null : totals.contributedUsd > 0 ? (
          <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.45 }}>
            Return on money in: {pctSigned(totals.returnOnMoneyInPct)}. This ignores when the money
            arrived, so it is not the same as your return.
          </div>
        ) : (
          <button
            className="btn btn-ghost btn-sm"
            style={{
              marginTop: 10, padding: '8px 0', minHeight: 0,
              display: 'block', textAlign: 'left', lineHeight: 1.45,
            }}
            onClick={onOpenDeposits}
          >
            <span style={{ fontSize: 12.5 }}>Record what you paid in →</span>
            <span className="dim" style={{ display: 'block', fontSize: 11, fontWeight: 400, marginTop: 3 }}>
              Start with your starting capital, and the second line appears across the whole chart.
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
